import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '@/store';
import {
  selectOrganizationUsers,
  selectOrgLoading,
  selectOrgLoadingAny,
} from '@/store/selectors/organization';
import { fetchOrgUsers } from '@/store/thunks/organization/fetchOrgUsers';
import { createUserThunk } from '@/store/thunks/userForm/createUserThunk';
import { updateUserThunk } from '@/store/thunks/userForm/updateUserThunk';
import { fetchInvitationLinkThunk } from '@/store/thunks/userForm/fetchInvitationLinkThunk';
import { useModal } from '@/hooks/useModal';
import { useUserDialogs } from '@/hooks/useUserDialogs';
import type { PortalUserValues, Scope, UserRow } from '@/types/users.types';
import { normalizeOrgUsersStatus, OrgUserStatus } from '@/types/status.types';

const MIN_SEARCH_LENGTH = 0;

export function useUsersPage() {
  const dispatch = useAppDispatch();

  // global store
  const users = useSelector(selectOrganizationUsers);
  const loadingMap = useSelector(selectOrgLoading);
  const loadingAny = useSelector(selectOrgLoadingAny);

  // UI hooks (Redux-backed)
  const { modal, isType, close: closeModal } = useModal();
  const {
    openCreateUser,
    openEditUser,
    openUserCreated,
    confirmToggleActive,
    confirmForceLogout,
    confirmForceLoginReset,
    confirmDelete,
  } = useUserDialogs();

  // local view state (filters/search)
  const [search, setSearch] = useState<string>('');
  const [scope, setScope] = useState<Scope>('all');
  const [deptValues, setDeptValues] = useState<string[]>([]);
  const [statusValues, setStatusValues] = useState<string[]>([]);

  // modal flags
  const isCreate = isType('createUser');
  const isEdit = isType('editUser');
  const isUserCreated = isType('userCreated');
   const editingUser: UserRow | undefined =
    isEdit && (modal !== null) && 'user' in modal ? (modal.user) : undefined;

  // data load on mount
  useEffect(() => {
    void dispatch(fetchOrgUsers());
  }, [dispatch]);

  // normalize store to UserRow
  const normalizedRows: UserRow[] = useMemo(() => {
 const asStatus = (value: unknown): UserRow['status'] =>
    normalizeOrgUsersStatus(value, OrgUserStatus.Inactive);

  const asUserType = (value: unknown): UserRow['userType'] =>
    value === 'Internal' || value === 'External' ? (value) : 'Internal';

    return (users).map((user) => ({
      id: user.id,
      name: user.name,
      email: user.email,
      phone: user.phone,
      deptOrg: user.deptOrg,
      status: asStatus(user.status),
      userType: asUserType((user).userType),
    }));
  }, [users]);

  // select options from rows
  const { deptOptions, statusOptions } = useMemo(() => {
    const deptSet = new Set<string>();
    const statusSet = new Set<string>();

    for (const row of normalizedRows) {
      if (row.deptOrg.length > MIN_SEARCH_LENGTH) deptSet.add(row.deptOrg);
      if (row.status.length > MIN_SEARCH_LENGTH) statusSet.add(row.status);
    }

    return {
      deptOptions: ['Dept / Org', ...Array.from(deptSet).sort()],
      statusOptions: ['Status', ...Array.from(statusSet).sort()],
    };
  }, [normalizedRows]);

  // filtered rows (view-only logic is fine to keep here)
  const filteredRows: UserRow[] = useMemo(() => {
    const query = search.trim().toLowerCase();

    return normalizedRows.filter((row) => {
      if (scope !== 'all' && row.userType.toLowerCase() !== scope) return false;
      if (deptValues.length > MIN_SEARCH_LENGTH && !deptValues.includes(row.deptOrg)) return false;
      if (statusValues.length > MIN_SEARCH_LENGTH && !statusValues.includes(row.status)) return false;

      if (query.length > MIN_SEARCH_LENGTH) {
        const haystack = [row.name, row.email, row.phone, row.deptOrg, row.status]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });
  }, [normalizedRows, scope, deptValues, statusValues, search]);

  // actions exposed to page (no dispatch in the page)
  const openCreate = useCallback(() => { openCreateUser(); }, [openCreateUser]);
  const editRow = useCallback((row: UserRow) => { openEditUser(row); }, [openEditUser]);

  const toggleActive = useCallback(
    (row: UserRow) => { confirmToggleActive(row); },
    [confirmToggleActive]
  );
  const forceLogout = useCallback(
    (row: UserRow) => { confirmForceLogout(row); },
    [confirmForceLogout]
  );
  const forceLoginReset = useCallback(
    (row: UserRow) => { confirmForceLoginReset(row); },
    [confirmForceLoginReset]
  );
  const removeUser = useCallback((row: UserRow) => { confirmDelete(row); }, [confirmDelete]);

  const submit = useCallback(
    async(values: PortalUserValues) => {
      const normalizedValues = { ...values, email: values.email.toLowerCase() };
      try {
        if (isCreate) {
          const createdUser = await dispatch(createUserThunk(normalizedValues)).unwrap();
          const { invitationLink, otp } = await dispatch(
            fetchInvitationLinkThunk({ userId: createdUser.userId })
          ).unwrap();
          openUserCreated(invitationLink, otp);
        } else if (isEdit && editingUser !== undefined) { // !== check produce error in build
          await dispatch(updateUserThunk({ id: editingUser.id, data: normalizedValues })).unwrap();
          closeModal();
        }
        void dispatch(fetchOrgUsers());
      } catch {
        // errors surfaced via thunks/slices
      }
    },
    [dispatch, isCreate, isEdit, editingUser, openUserCreated, closeModal]
  );

  return {
    // view data
    rows: filteredRows,
    loading: loadingAny || loadingMap.orgUsers,
    deptOptions,
    statusOptions,

    // modal state
    modal,
    isCreate,
    isEdit,
    isUserCreated,
    editingUser,
    closeModal,

    // header/filter controls
    search,
    setSearch,
    scope,
    setScope,
    deptValues,
    setDeptValues,
    statusValues,
    setStatusValues,

    // actions
    openCreate,
    editRow,
    toggleActive,
    forceLogout,
    forceLoginReset,
    removeUser,
    submit,
  };
}
